import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import { useDashboardTheme } from "../../../context/DashboardThemeContext";

const ENDPOINTS = {
  puntoCreate: "/api/facturacion/config/puntos/create/",
  puntoDelete: "/api/facturacion/config/puntos/delete/",
  secuencialUpdate: "/api/facturacion/config/secuenciales/update/",
};

const normalizeCode = (value) => {
  const only = String(value || "").replace(/\D/g, "").slice(0, 3);
  return only ? only.padStart(3, "0") : "";
};

const toInt = (value) => {
  const number = parseInt(String(value ?? "").replace(/\D/g, ""), 10);
  return Number.isFinite(number) ? number : 0;
};

const formatSecuencial = (value) => {
  return String(toInt(value) || 1).padStart(9, "0");
};

const EmissionAndSequenceSettings = ({
  establecimientos = [],
  puntos = [],
  secuenciales = [],
  onRefresh,
  authFetch,
  parseJsonSafe,
  notify,
  logout,
  fieldSx,
  primaryButtonSx,
  outlinedButtonSx,
}) => {
  const { dashboardTheme } = useDashboardTheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [puntoOpen, setPuntoOpen] = useState(false);
  const [puntoForm, setPuntoForm] = useState({
    establecimiento: "",
    punto_emision: "",
  });

  const [secuencialOpen, setSecuencialOpen] = useState(false);
  const [secForm, setSecForm] = useState({
    establecimiento: "",
    punto_emision: "",
    secuencia_inicial: "000000001",
  });

  const [savingPunto, setSavingPunto] = useState(false);
  const [savingSecuencial, setSavingSecuencial] = useState(false);
  const [deletingKey, setDeletingKey] = useState("");

  const cardSx = {
    borderRadius: "14px",
    p: 2,
    border: `1px solid ${dashboardTheme.border_color}`,
    bgcolor: dashboardTheme.card_background,
    boxShadow:
      dashboardTheme.theme_mode === "dark"
        ? "none"
        : "0 4px 10px rgba(0,0,0,0.08)",
  };

  const modalBoxSx = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: dashboardTheme.card_background,
    border: `1px solid ${dashboardTheme.border_color}`,
    color: dashboardTheme.text_color,
    p: { xs: 2, sm: 3 },
    borderRadius: "16px",
    width: "min(760px, calc(100vw - 32px))",
    boxShadow:
      dashboardTheme.theme_mode === "dark"
        ? "0 18px 50px rgba(0,0,0,0.55)"
        : "0 18px 50px rgba(0,0,0,0.18)",
    outline: "none",
  };

  const paperItemSx = {
    p: 1.5,
    borderRadius: "12px",
    border: `1px solid ${dashboardTheme.border_color}`,
    bgcolor: dashboardTheme.table_background || dashboardTheme.card_background,
  };

  const iconButtonSx = {
    bgcolor:
      dashboardTheme.theme_mode === "dark"
        ? "rgba(255,255,255,0.06)"
        : `${dashboardTheme.primary_color}12`,
    "&:hover": {
      bgcolor:
        dashboardTheme.theme_mode === "dark"
          ? "rgba(255,255,255,0.1)"
          : `${dashboardTheme.primary_color}20`,
    },
  };

  const establecimientosUnicos = useMemo(() => {
    const map = new Map();

    (establecimientos || []).forEach((item) => {
      const codigo = normalizeCode(item.codigo);
      if (!codigo) return;

      map.set(codigo, {
        id: item.id,
        codigo,
        nombre: item.nombre || codigo,
        activo: item.activo !== false,
      });
    });

    (puntos || []).forEach((item) => {
      const codigo = normalizeCode(item.establecimiento);
      if (!codigo || map.has(codigo)) return;

      map.set(codigo, {
        codigo,
        nombre: item.establecimiento_nombre || codigo,
        activo: true,
      });
    });

    return Array.from(map.values())
      .filter((item) => item.activo)
      .sort((a, b) => a.codigo.localeCompare(b.codigo));
  }, [establecimientos, puntos]);

  const puntosUnicos = useMemo(() => {
    const seen = new Set();

    return (puntos || [])
      .map((item) => ({
        id: item.id,
        establecimiento: normalizeCode(item.establecimiento),
        establecimiento_nombre: item.establecimiento_nombre || "",
        punto_emision: normalizeCode(item.punto_emision),
        activo: item.activo !== false,
      }))
      .filter((item) => {
        if (!item.activo || !item.establecimiento || !item.punto_emision) return false;

        const key = `${item.establecimiento}-${item.punto_emision}`;
        if (seen.has(key)) return false;

        seen.add(key);
        return true;
      })
      .sort((a, b) => {
        if (a.establecimiento !== b.establecimiento) {
          return a.establecimiento.localeCompare(b.establecimiento);
        }

        return a.punto_emision.localeCompare(b.punto_emision);
      });
  }, [puntos]);

  const puntosAgrupados = useMemo(() => {
    const map = new Map();

    puntosUnicos.forEach((item) => {
      if (!map.has(item.establecimiento)) {
        const est = establecimientosUnicos.find((x) => x.codigo === item.establecimiento);

        map.set(item.establecimiento, {
          establecimiento: item.establecimiento,
          nombre: est?.nombre || item.establecimiento_nombre || item.establecimiento,
          puntos: [],
        });
      }

      map.get(item.establecimiento).puntos.push(item);
    });

    return Array.from(map.values()).sort((a, b) =>
      a.establecimiento.localeCompare(b.establecimiento)
    );
  }, [puntosUnicos, establecimientosUnicos]);

  const secuencialesUnicos = useMemo(() => {
    const map = new Map();

    (secuenciales || []).forEach((item) => {
      const establecimiento = normalizeCode(item.establecimiento);
      const punto = normalizeCode(item.punto_emision || item.punto);
      const actual = toInt(item.secuencial_actual);
      const proximo = item.proximo || formatSecuencial(actual + 1);
      const activo = item.activo !== false;

      if (!activo || !establecimiento || !punto) return;

      const key = `${establecimiento}-${punto}`;

      map.set(key, {
        id: item.id,
        tipo_documento: "01",
        establecimiento,
        punto_emision: punto,
        secuencial_actual: actual,
        proximo,
        secuencia_inicial: item.secuencia_inicial || actual + 1,
        activo,
      });
    });

    return Array.from(map.values()).sort((a, b) => {
      if (a.establecimiento !== b.establecimiento) {
        return a.establecimiento.localeCompare(b.establecimiento);
      }

      return a.punto_emision.localeCompare(b.punto_emision);
    });
  }, [secuenciales]);

  const secuencialesAgrupados = useMemo(() => {
    const map = new Map();

    secuencialesUnicos.forEach((item) => {
      const key = `${item.establecimiento}-${item.punto_emision}`;

      if (!map.has(key)) {
        const est = establecimientosUnicos.find((x) => x.codigo === item.establecimiento);

        map.set(key, {
          key,
          establecimiento: item.establecimiento,
          establecimiento_nombre: est?.nombre || item.establecimiento,
          punto_emision: item.punto_emision,
          secuenciales: [],
        });
      }

      map.get(key).secuenciales.push(item);
    });

    return Array.from(map.values()).sort((a, b) => {
      if (a.establecimiento !== b.establecimiento) {
        return a.establecimiento.localeCompare(b.establecimiento);
      }

      return a.punto_emision.localeCompare(b.punto_emision);
    });
  }, [secuencialesUnicos, establecimientosUnicos]);

  const puntosDelEstablecimiento = useMemo(() => {
    return puntosUnicos.filter((item) => item.establecimiento === secForm.establecimiento);
  }, [puntosUnicos, secForm.establecimiento]);

  const getSecuencialActual = (establecimiento, punto) => {
    const est = normalizeCode(establecimiento);
    const pto = normalizeCode(punto);

    const match = secuencialesUnicos.find(
      (item) => item.establecimiento === est && item.punto_emision === pto
    );

    return match ? formatSecuencial(match.proximo || match.secuencia_inicial) : "000000001";
  };

  const openPuntoModal = () => {
    setPuntoForm({
      establecimiento: establecimientosUnicos[0]?.codigo || "001",
      punto_emision: "",
    });
    setPuntoOpen(true);
  };

  const openSecuencialModal = (item = null) => {
    if (item) {
      setSecForm({
        establecimiento: normalizeCode(item.establecimiento),
        punto_emision: normalizeCode(item.punto_emision),
        secuencia_inicial: formatSecuencial(item.proximo || item.secuencia_inicial),
      });

      setSecuencialOpen(true);
      return;
    }

    const primerPunto = puntosUnicos[0];
    const establecimiento = primerPunto?.establecimiento || establecimientosUnicos[0]?.codigo || "001";
    const punto = primerPunto?.punto_emision || "";

    setSecForm({
      establecimiento,
      punto_emision: punto,
      secuencia_inicial: getSecuencialActual(establecimiento, punto),
    });

    setSecuencialOpen(true);
  };

  const addPunto = async () => {
    try {
      const establecimiento = normalizeCode(puntoForm.establecimiento);
      const punto = normalizeCode(puntoForm.punto_emision);

      if (!establecimiento) {
        notify("Seleccione un establecimiento.", "error");
        return;
      }

      if (!punto || punto === "000") {
        notify("Ingrese un punto de emisión válido.", "error");
        return;
      }

      setSavingPunto(true);

      const payload = {
        establecimiento,
        punto_emision: punto,
      };

      const res = await authFetch(ENDPOINTS.puntoCreate, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (!res.ok || !data.ok) {
        notify(data?.error || "No se pudo guardar el punto de emisión.", "error");
        return;
      }

      setPuntoOpen(false);
      await onRefresh();
      notify(data?.message || "Punto de emisión guardado correctamente.", "success");
    } catch {
      notify("Error de conexión", "error");
    } finally {
      setSavingPunto(false);
    }
  };

  const deletePunto = async (item) => {
    try {
      const key = `${item.establecimiento}-${item.punto_emision}`;
      setDeletingKey(key);

      const res = await authFetch(ENDPOINTS.puntoDelete, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          establecimiento: item.establecimiento,
          punto_emision: item.punto_emision,
        }),
      });

      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (!res.ok || !data.ok) {
        notify(data?.error || "No se pudo eliminar el punto de emisión.", "error");
        return;
      }

      await onRefresh();
      notify(data?.message || "Punto de emisión eliminado correctamente.", "success");
    } catch {
      notify("Error de conexión", "error");
    } finally {
      setDeletingKey("");
    }
  };

  const addSecuencial = async () => {
    try {
      const establecimiento = normalizeCode(secForm.establecimiento);
      const punto = normalizeCode(secForm.punto_emision);
      const secuencia = formatSecuencial(secForm.secuencia_inicial);

      if (!establecimiento) {
        notify("Seleccione un establecimiento.", "error");
        return;
      }

      if (!punto) {
        notify("Seleccione un punto de emisión.", "error");
        return;
      }

      if (toInt(secuencia) < 1) {
        notify("El secuencial inicial debe ser mayor a cero.", "error");
        return;
      }

      setSavingSecuencial(true);

      const res = await authFetch(ENDPOINTS.secuencialUpdate, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          tipo_documento: "01",
          establecimiento,
          punto_emision: punto,
          secuencia_inicial: secuencia,
        }),
      });

      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (!res.ok || !data.ok) {
        notify(data?.error || "No se pudo guardar el secuencial.", "error");
        return;
      }

      setSecuencialOpen(false);
      await onRefresh();
      notify(data?.message || "Secuencial guardado correctamente.", "success");
    } catch {
      notify("Error de conexión", "error");
    } finally {
      setSavingSecuencial(false);
    }
  };

  const deleteSecuencial = async (item) => {
    try {
      const key = `sec-${item.establecimiento}-${item.punto_emision}`;
      setDeletingKey(key);

      const res = await authFetch(ENDPOINTS.secuencialUpdate, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          eliminar: 1,
          tipo_documento: "01",
          establecimiento: item.establecimiento,
          punto_emision: item.punto_emision,
        }),
      });

      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (!res.ok || !data.ok) {
        notify(data?.error || "No se pudo eliminar el secuencial.", "error");
        return;
      }

      await onRefresh();
      notify(data?.message || "Secuencial eliminado correctamente.", "success");
    } catch {
      notify("Error de conexión", "error");
    } finally {
      setDeletingKey("");
    }
  };

  const handleSecEstablecimientoChange = (value) => {
    const establecimiento = normalizeCode(value);
    const primerPunto = puntosUnicos.find((item) => item.establecimiento === establecimiento)?.punto_emision || "";

    setSecForm({
      establecimiento,
      punto_emision: primerPunto,
      secuencia_inicial: getSecuencialActual(establecimiento, primerPunto),
    });
  };

  const handleSecPuntoChange = (value) => {
    const punto = normalizeCode(value);

    setSecForm((prev) => ({
      ...prev,
      punto_emision: punto,
      secuencia_inicial: getSecuencialActual(prev.establecimiento, punto),
    }));
  };

  return (
    <Paper elevation={0} sx={cardSx}>
      <Stack spacing={2}>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor:
                  dashboardTheme.theme_mode === "dark"
                    ? "rgba(255,255,255,0.06)"
                    : `${dashboardTheme.primary_color}14`,
              }}
            >
              <ReceiptLongRoundedIcon sx={{ color: dashboardTheme.primary_color }} />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: { xs: 17, md: 18 },
                  fontWeight: 600,
                  color: dashboardTheme.text_color,
                }}
              >
                Establecimientos, puntos y secuenciales
              </Typography>

              <Typography
                sx={{
                  fontSize: 13,
                  color: dashboardTheme.muted_text_color,
                  lineHeight: 1.5,
                }}
              >
                Configura desde dónde se emitirán las facturas electrónicas.
              </Typography>
            </Box>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", md: "auto" } }}>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={openPuntoModal}
              sx={primaryButtonSx}
            >
              Añadir punto
            </Button>

            <Button
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              onClick={() => openSecuencialModal()}
              disabled={!puntosUnicos.length}
              sx={outlinedButtonSx}
            >
              Añadir secuencia
            </Button>
          </Stack>
        </Box>

        <Stack spacing={1.5}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: dashboardTheme.text_color }}>
            Puntos de emisión
          </Typography>

          {puntosAgrupados.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: "12px",
                border: `1px dashed ${dashboardTheme.border_color}`,
                bgcolor: dashboardTheme.table_background || dashboardTheme.card_background,
              }}
            >
              <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
                No hay puntos de emisión creados.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
              {puntosAgrupados.map((grupo) => (
                <Paper key={grupo.establecimiento} elevation={0} sx={paperItemSx}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <StorefrontRoundedIcon fontSize="small" sx={{ color: dashboardTheme.primary_color }} />

                    <Box>
                      <Typography sx={{ fontWeight: 700, color: dashboardTheme.text_color, lineHeight: 1.1 }}>
                        Establecimiento {grupo.establecimiento}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: dashboardTheme.muted_text_color }}>
                        {grupo.nombre}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {grupo.puntos.map((item) => {
                      const key = `${item.establecimiento}-${item.punto_emision}`;
                      const deleting = deletingKey === key;

                      return (
                        <Box
                          key={key}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.25,
                            py: 0.75,
                            border: `1px solid ${dashboardTheme.border_color}`,
                            borderRadius: "10px",
                            bgcolor: dashboardTheme.card_background,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: dashboardTheme.text_color }}>
                            P. Emisión {item.punto_emision}
                          </Typography>

                          <Tooltip title="Eliminar punto">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => deletePunto(item)}
                                disabled={deleting}
                                sx={iconButtonSx}
                              >
                                {deleting ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <DeleteOutlineRoundedIcon fontSize="small" sx={{ color: "#dc2626" }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Stack>

        <Divider sx={{ borderColor: dashboardTheme.border_color }} />

        <Stack spacing={1.5}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: dashboardTheme.text_color }}>
            Secuencial de factura
          </Typography>

          {!puntosUnicos.length ? (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: "12px",
                border: `1px dashed ${dashboardTheme.border_color}`,
                bgcolor: dashboardTheme.table_background || dashboardTheme.card_background,
              }}
            >
              <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
                Primero crea un punto de emisión para configurar el secuencial.
              </Typography>
            </Paper>
          ) : secuencialesAgrupados.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: "12px",
                border: `1px dashed ${dashboardTheme.border_color}`,
                bgcolor: dashboardTheme.table_background || dashboardTheme.card_background,
              }}
            >
              <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
                No hay secuencial de factura configurado.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" }, gap: 1.5 }}>
              {secuencialesAgrupados.map((grupo) => (
                <Paper key={grupo.key} elevation={0} sx={paperItemSx}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <ReceiptLongRoundedIcon fontSize="small" sx={{ color: dashboardTheme.primary_color }} />

                    <Box>
                      <Typography sx={{ fontWeight: 700, color: dashboardTheme.text_color, lineHeight: 1.1 }}>
                        {grupo.establecimiento_nombre}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: dashboardTheme.muted_text_color }}>
                        Est. {grupo.establecimiento} · P. Emisión {grupo.punto_emision}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 1, borderColor: dashboardTheme.border_color }} />

                  {grupo.secuenciales.map((item) => {
                    const key = `sec-${item.establecimiento}-${item.punto_emision}`;
                    const deleting = deletingKey === key;

                    return (
                      <Box
                        key={key}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          px: 1.25,
                          py: 1,
                          border: `1px solid ${dashboardTheme.border_color}`,
                          borderRadius: "10px",
                          bgcolor: dashboardTheme.card_background,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, color: dashboardTheme.text_color, lineHeight: 1.15 }}>
                            Factura
                          </Typography>
                          <Typography sx={{ fontSize: 13, color: dashboardTheme.muted_text_color, fontWeight: 600 }}>
                            Próxima: {formatSecuencial(item.proximo || item.secuencia_inicial)}
                          </Typography>
                        </Box>

                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Editar secuencia">
                            <IconButton size="small" onClick={() => openSecuencialModal(item)} sx={iconButtonSx}>
                              <EditRoundedIcon fontSize="small" sx={{ color: dashboardTheme.primary_color }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Eliminar secuencial">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => deleteSecuencial(item)}
                                disabled={deleting}
                                sx={iconButtonSx}
                              >
                                {deleting ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <DeleteOutlineRoundedIcon fontSize="small" sx={{ color: "#dc2626" }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Box>
                    );
                  })}
                </Paper>
              ))}
            </Box>
          )}
        </Stack>
      </Stack>

      <Modal open={puntoOpen} onClose={() => setPuntoOpen(false)}>
        <Box sx={modalBoxSx}>
          <Typography sx={{ fontWeight: 700, mb: 0.5, fontSize: 18, color: dashboardTheme.text_color }}>
            Crear punto de emisión
          </Typography>

          <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 13, mb: 2 }}>
            Selecciona el establecimiento y escribe el código del punto de emisión.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 2,
            }}
          >
            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel>Establecimiento</InputLabel>
              <Select
                label="Establecimiento"
                value={puntoForm.establecimiento}
                onChange={(event) =>
                  setPuntoForm((prev) => ({
                    ...prev,
                    establecimiento: normalizeCode(event.target.value),
                  }))
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: dashboardTheme.card_background,
                      color: dashboardTheme.text_color,
                      border: `1px solid ${dashboardTheme.border_color}`,
                    },
                  },
                }}
              >
                {establecimientosUnicos.map((item) => (
                  <MenuItem key={item.codigo} value={item.codigo}>
                    {item.codigo} - {item.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              label="Punto de emisión"
              value={puntoForm.punto_emision}
              placeholder="001"
              onChange={(event) =>
                setPuntoForm((prev) => ({
                  ...prev,
                  punto_emision: event.target.value.replace(/\D/g, "").slice(0, 3),
                }))
              }
              sx={fieldSx}
            />
          </Box>

          <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={() => setPuntoOpen(false)} disabled={savingPunto} sx={outlinedButtonSx}>
              Cancelar
            </Button>

            <Button
              variant="contained"
              onClick={addPunto}
              disabled={savingPunto}
              sx={primaryButtonSx}
            >
              {savingPunto ? "Guardando..." : "Guardar"}
            </Button>
          </Stack>
        </Box>
      </Modal>

      <Modal open={secuencialOpen} onClose={() => setSecuencialOpen(false)}>
        <Box sx={modalBoxSx}>
          <Typography sx={{ fontWeight: 700, mb: 0.5, fontSize: 18, color: dashboardTheme.text_color }}>
            Configurar secuencial de factura
          </Typography>

          <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 13, mb: 2 }}>
            Define la próxima numeración de facturas para el establecimiento y punto de emisión.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 2,
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Tipo de documento"
              value="Factura"
              disabled
              sx={fieldSx}
            />

            <TextField
              fullWidth
              size="small"
              label="Secuencial inicial"
              value={secForm.secuencia_inicial}
              placeholder="000000001"
              onChange={(event) =>
                setSecForm((prev) => ({
                  ...prev,
                  secuencia_inicial: event.target.value.replace(/\D/g, "").slice(0, 9),
                }))
              }
              sx={fieldSx}
            />

            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel>Establecimiento</InputLabel>
              <Select
                label="Establecimiento"
                value={secForm.establecimiento}
                onChange={(event) => handleSecEstablecimientoChange(event.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: dashboardTheme.card_background,
                      color: dashboardTheme.text_color,
                      border: `1px solid ${dashboardTheme.border_color}`,
                    },
                  },
                }}
              >
                {establecimientosUnicos.map((item) => (
                  <MenuItem key={item.codigo} value={item.codigo}>
                    {item.codigo} - {item.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel>Punto de emisión</InputLabel>
              <Select
                label="Punto de emisión"
                value={secForm.punto_emision}
                onChange={(event) => handleSecPuntoChange(event.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: dashboardTheme.card_background,
                      color: dashboardTheme.text_color,
                      border: `1px solid ${dashboardTheme.border_color}`,
                    },
                  },
                }}
              >
                {puntosDelEstablecimiento.map((item) => (
                  <MenuItem key={`${item.establecimiento}-${item.punto_emision}`} value={item.punto_emision}>
                    {item.punto_emision}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Paper
            elevation={0}
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: "12px",
              border: `1px solid ${dashboardTheme.border_color}`,
              bgcolor: dashboardTheme.table_background || dashboardTheme.card_background,
            }}
          >
            <Typography sx={{ fontSize: 13, color: dashboardTheme.muted_text_color, fontWeight: 600 }}>
              Vista previa
            </Typography>

            <Typography sx={{ fontSize: 15, color: dashboardTheme.text_color, fontWeight: 700, mt: 0.5 }}>
              Factura · {normalizeCode(secForm.establecimiento)} - {normalizeCode(secForm.punto_emision)} -{" "}
              {formatSecuencial(secForm.secuencia_inicial)}
            </Typography>
          </Paper>

          <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => setSecuencialOpen(false)}
              disabled={savingSecuencial}
              sx={outlinedButtonSx}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              onClick={addSecuencial}
              disabled={savingSecuencial}
              sx={primaryButtonSx}
            >
              {savingSecuencial ? "Guardando..." : "Guardar"}
            </Button>
          </Stack>
        </Box>
      </Modal>
    </Paper>
  );
};

export default EmissionAndSequenceSettings;